/*
 * WOW Bundle - our own bundle builder, replacing Rebuy's Bundle Builder on
 * /pages/bundle-builder.
 *
 * Group A (Rebuy): this file does nothing.
 * Group B (ours):  this file is the builder - but only while the bundle
 *                  discount is live in Shopify (see PRICE SAFETY below).
 *
 * The group comes from snippets/wow-cart-head.liquid (window.WowCartConfig);
 * the products, tiers and wording come from snippets/wow-bundle.liquid.
 * Source of truth: storefront/wow-bundle/ in the WOW repo - never edit this in
 * the Shopify theme editor, the next deploy overwrites it.
 *
 * PRICE SAFETY. The builder shows discounted prices, so the cart has to charge
 * them. Three guards:
 *   1. discountLive (set in the snippet) is false until the discount exists in
 *      Shopify. While false the builder stays off and Rebuy's is left alone.
 *   2. After every add the cart is read back and compared with what the
 *      builder showed. A gift that came back with a price is taken out again.
 *   3. If the cart charged more than shown, the builder stands down for the
 *      rest of the visit and the shopper gets Rebuy's builder back.
 */
(function (root) {
  'use strict';

  /* ---------- pure helpers (unit-tested in storefront/wow-bundle/test) ---------- */

  function formatMoney(cents, format) {
    var value = (Number(cents) || 0) / 100;
    var fmt = format || '${{amount}}';
    function withCommas(n, decimals) {
      var parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      if (key === 'amount_no_decimals') return withCommas(Math.round(value), 0);
      return withCommas(value, 2);
    });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // The highest tier the bundle has reached, or -1 for none.
  function activeTierIndex(count, tiers) {
    var found = -1;
    (tiers || []).forEach(function (t, i) { if (count >= t.quantity) found = i; });
    return found;
  }

  function tierPercent(count, tiers) {
    var i = activeTierIndex(count, tiers);
    return i === -1 ? 0 : Number(tiers[i].percent) || 0;
  }

  // Rebuy rounds the discounted PRICE (not the discount) to the nearest cent,
  // halves up: $43.05 at 10% off shows $38.75. Done in whole numbers so 12.5%
  // cannot drift.
  function discountedPrice(cents, percent) {
    var tenths = Math.round((Number(percent) || 0) * 10);
    return Math.round((Number(cents) || 0) * (1000 - tenths) / 1000);
  }

  // "5%", "12.5%"
  function percentLabel(percent) {
    return String(Number(percent)) + '%';
  }

  // "Buy [quantity] and unlock [discount]" -> "Buy 8 and unlock 12.5%"
  function fillTemplate(template, tier) {
    return String(template || '')
      .replace(/\[quantity\]/g, String(tier.quantity))
      .replace(/\[discount\]/g, percentLabel(tier.percent));
  }

  // What a product card or a bundle row shows. `compare` is null when there is
  // nothing to strike through.
  function displayPrice(variant, percent) {
    var price = Number(variant.price) || 0;
    var compareAt = Number(variant.compare_at_price) || 0;
    if (percent > 0) {
      return { price: discountedPrice(price, percent), compare: Math.max(compareAt, price) };
    }
    return { price: price, compare: compareAt > price ? compareAt : null };
  }

  // Totals for the button and the savings line. `items` are variants, one per
  // unit in the bundle. The struck-through total is the sum of the plain
  // prices (not the compare-at prices), as Rebuy shows it. The discounted
  // total is the discount taken off that sum, NOT the sum of the rows: eleven
  // items that add up to $318.98 row by row show as $318.99, as on Rebuy's.
  // (Shopify discounts line by line, so the cart can come out a cent under.)
  function bundleTotals(items, tiers) {
    var percent = tierPercent(items.length, tiers);
    var original = 0;
    items.forEach(function (v) { original += Number(v.price) || 0; });
    var discounted = discountedPrice(original, percent);
    return { percent: percent, original: original, discounted: discounted, savings: original - discounted };
  }

  // The bar is full at the last tier; the markers sit 4 points left of their
  // true spot so the last one stays inside the bar (16/36/56/76/96).
  function progressPercent(count, tiers) {
    var last = tiers && tiers.length ? tiers[tiers.length - 1].quantity : 0;
    if (!last) return 0;
    return Math.max(0, Math.min(100, Math.round((count / last) * 100)));
  }

  function tierPosition(tier, tiers) {
    var last = tiers[tiers.length - 1].quantity;
    return Math.round((tier.quantity / last) * 100) - 4;
  }

  function canAdd(count, maxItems, quantity) {
    return count + (quantity || 1) <= maxItems;
  }

  // The gift on offer: only the tier the bundle is in right now counts, the
  // gifts of the tiers below it are not kept.
  function giftTier(count, tiers) {
    var i = activeTierIndex(count, tiers);
    return i !== -1 && tiers[i].gift ? i : -1;
  }

  // Keep what can still be bought, in the order Rebuy shows it.
  function prepareProducts(products, handles) {
    var order = handles || [];
    return (products || []).map(function (p) {
      var buyable = (p.variants || []).filter(function (v) { return v.available && Number(v.price) > 0; });
      var copy = {};
      Object.keys(p).forEach(function (k) { copy[k] = p[k]; });
      copy.variants = buyable;
      return copy;
    }).filter(function (p) {
      return p.available !== false && p.variants.length > 0;
    }).sort(function (a, b) {
      var ia = order.indexOf(a.handle), ib = order.indexOf(b.handle);
      return (ia === -1 ? 9999 : ia) - (ib === -1 ? 9999 : ib);
    });
  }

  // A saved bundle from an earlier visit: drop what is gone, keep to the limit.
  function restoreBundle(saved, knownVariantIds, maxItems) {
    if (!Array.isArray(saved)) return [];
    return saved.map(Number).filter(function (id) {
      return knownVariantIds.indexOf(id) !== -1;
    }).slice(0, maxItems);
  }

  function removeLast(bundle, variantId) {
    var at = bundle.lastIndexOf(variantId);
    if (at === -1) return bundle.slice();
    return bundle.slice(0, at).concat(bundle.slice(at + 1));
  }

  function countOf(bundle, variantId) {
    return bundle.filter(function (id) { return id === variantId; }).length;
  }

  // The body for /cart/add.js. Every line carries the two properties the rest
  // of the WOW storefront looks for; the gift carries one more so it can be
  // found again when the cart is checked.
  //
  // `rebuyWidgetId` (optional) also stamps the lines the way Rebuy's own builder
  // does, so Rebuy's discount applies to them. Borrowed for the length of the
  // side-by-side test (Kale, 2026-09-30); it stops working the day Rebuy is
  // cancelled. verifyCart still checks every add.
  function lineProperties(bundleId, rebuyWidgetId, gift) {
    var props = { _wow_src: 'bundle', _wow_bundle: bundleId };
    if (gift) props._wow_bundle_gift = '1';
    if (rebuyWidgetId) {
      // copied off real Rebuy bundle lines, 2026-09-30; the gift's differ
      if (!gift) props._r_bbsi = 0;
      props._r_added_bundle_session_id = bundleId;
      props._source = 'Rebuy';
      props._widget_id = String(rebuyWidgetId);
      props._attribution = gift ? 'Rebuy Bundle Builder Gift With Purchase' : 'Rebuy Bundle Builder';
    }
    return props;
  }

  function cartItems(bundle, giftVariantId, bundleId, rebuyWidgetId) {
    var seen = [], items = [];
    bundle.forEach(function (id) {
      var at = seen.indexOf(id);
      if (at === -1) {
        seen.push(id);
        items.push({ id: id, quantity: 1, properties: lineProperties(bundleId, rebuyWidgetId, false) });
      } else {
        items[at].quantity += 1;
      }
    });
    if (giftVariantId) {
      items.push({ id: giftVariantId, quantity: 1, properties: lineProperties(bundleId, rebuyWidgetId, true) });
    }
    return items;
  }

  // Compare the cart with what the builder showed. `ok` is false when the
  // shopper would pay more than shown. A cent per line of rounding is let
  // through; paying less is never a problem.
  function verifyCart(cart, bundleId, expectedCents) {
    var lines = ((cart && cart.items) || []).filter(function (i) {
      return i.properties && i.properties._wow_bundle === bundleId;
    });
    var paid = lines.filter(function (i) { return !i.properties._wow_bundle_gift; });
    var gifts = lines.filter(function (i) { return i.properties._wow_bundle_gift; });
    var actual = paid.reduce(function (sum, i) { return sum + i.final_line_price; }, 0);
    var chargedGifts = gifts.filter(function (i) { return i.final_line_price > 0; });
    var found = paid.length > 0;
    return {
      found: found,
      actual: actual,
      expected: expectedCents,
      chargedGiftKeys: chargedGifts.map(function (i) { return i.key; }),
      ok: found && actual <= expectedCents + paid.length && chargedGifts.length === 0
    };
  }

  // Is the builder allowed to run? `preview` (?wow_bundle=preview) is for
  // testing on an unpublished theme only and never works on the live theme.
  function isLive(data, preview) {
    if (!data) return false;
    if (data.discountLive === true) return true;
    return preview === true && data.previewAllowed === true;
  }

  function newBundleId() {
    var s = '';
    for (var i = 0; i < 12; i++) s += Math.floor(Math.random() * 36).toString(36);
    return s;
  }

  function perPage(width) {
    if (width >= 768) return 3;
    return width > 480 ? 2 : 1;
  }

  // Arrows move a page at a time and wrap round at either end.
  function carouselStep(index, direction, total, per) {
    var max = Math.max(0, total - per);
    if (direction > 0) return index >= max ? 0 : Math.min(max, index + per);
    return index <= 0 ? max : Math.max(0, index - per);
  }

  var helpers = {
    formatMoney: formatMoney, escapeHtml: escapeHtml, activeTierIndex: activeTierIndex,
    tierPercent: tierPercent, discountedPrice: discountedPrice, percentLabel: percentLabel,
    fillTemplate: fillTemplate, displayPrice: displayPrice, bundleTotals: bundleTotals,
    progressPercent: progressPercent, tierPosition: tierPosition, canAdd: canAdd, giftTier: giftTier,
    prepareProducts: prepareProducts, restoreBundle: restoreBundle, removeLast: removeLast,
    countOf: countOf, cartItems: cartItems, verifyCart: verifyCart, isLive: isLive,
    newBundleId: newBundleId, perPage: perPage, carouselStep: carouselStep
  };
  if (typeof module !== 'undefined' && module.exports) { module.exports = helpers; return; }

  /* ---------- browser only from here ---------- */

  var CART = root.WowCartConfig;
  if (!CART || CART.arm !== 'B') return;   // group A: Rebuy's builder, untouched
  var doc = root.document;
  var STORE_KEY = 'wow_bundle_items_v1';
  var OFF_KEY = 'wow_bundle_stood_down';
  var PREVIEW_KEY = 'wow_bundle_preview';

  var DATA = null, T = {}, TIERS = [];
  var el = null, modal = null, overlay = null;
  var steps = [];              // [{title, description, products:[...]}]
  var byVariant = {};          // variant id -> {product, variant}
  var bundle = [];             // variant ids, one per unit, in the order added
  var chosen = {};             // product handle -> variant id picked on its card
  var giftPicked = {};         // tier index -> true once "Select Gift" was pressed
  var giftVariant = {};        // tier index -> variant id picked for the gift
  var carousels = [];
  var busy = false;
  var expanded = false;        // phone: is "My Bundle" pulled up
  var noDiscount = false;      // cart did not honour the prices and Rebuy is gone

  function money(c) { return formatMoney(c, DATA.moneyFormat); }

  var ICON = {
    up: '<svg class="wow-bundle__icon" viewBox="0 0 14 14" aria-hidden="true"><path d="M1.5 10L7 4.5l5.5 5.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    down: '<svg class="wow-bundle__icon" viewBox="0 0 14 14" aria-hidden="true"><path d="M1.5 4.5L7 10l5.5-5.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    plus: '<svg class="wow-bundle__icon" viewBox="0 0 14 14" aria-hidden="true"><path d="M1 7h12M7 1v12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    minus: '<svg class="wow-bundle__icon" viewBox="0 0 14 14" aria-hidden="true"><path d="M1 7h12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    times: '<svg class="wow-bundle__icon" viewBox="0 0 14 14" aria-hidden="true"><path d="M2 2l10 10M12 2L2 12" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linecap="round"/></svg>',
    arrow: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40" focusable="false" aria-hidden="true"><path d="m15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z"></path></svg>'
  };

  function img(src, width) {
    if (!src) return '';
    var u = String(src).replace(/^https?:/, '');
    u = u.replace(/([?&])width=\d+/, '$1width=' + width);
    if (u.indexOf('width=') === -1) u += (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    return u;
  }

  // XMLHttpRequest on purpose, not fetch: see the traps in
  // storefront/wow-cart/README.md (the shipping-protection app only re-prices
  // its button after an XMLHttpRequest). The WOW cart drawer watches for cart
  // adds made this way and opens itself.
  function api(path, body) {
    return new Promise(function (resolve, reject) {
      var xhr = new root.XMLHttpRequest();
      xhr.open(body ? 'POST' : 'GET', path);
      xhr.setRequestHeader('Accept', 'application/json');
      if (body) xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onload = function () {
        var json = null;
        try { json = JSON.parse(xhr.responseText); } catch (e) { /* handled below */ }
        if (xhr.status >= 200 && xhr.status < 300 && json) { resolve(json); return; }
        var err = new Error((json && (json.description || json.message)) || 'Cart error');
        err.data = json;
        reject(err);
      };
      xhr.onerror = function () { reject(new Error('Network error')); };
      xhr.send(body ? JSON.stringify(body) : null);
    });
  }

  /* ---------- state ---------- */

  function tiers() { return noDiscount ? [] : TIERS; }
  function percentNow() { return tierPercent(bundle.length, tiers()); }
  function bundleVariants() { return bundle.map(function (id) { return byVariant[id].variant; }); }

  function save() {
    try { root.localStorage.setItem(STORE_KEY, JSON.stringify(bundle)); } catch (e) { /* ignore */ }
  }

  function load() {
    var saved = null;
    try { saved = JSON.parse(root.localStorage.getItem(STORE_KEY) || 'null'); } catch (e) { /* ignore */ }
    var known = Object.keys(byVariant).map(Number);
    bundle = restoreBundle(saved, known, DATA.maxItems);
  }

  function currentGift() {
    var i = giftTier(bundle.length, tiers());
    if (i === -1) return null;
    var product = DATA.gifts && DATA.gifts[TIERS[i].gift];
    if (!product) return null;
    var allowed = TIERS[i].giftVariants || [];
    var variants = (product.variants || []).filter(function (v) {
      return v.available && allowed.indexOf(v.id) !== -1;
    });
    if (!variants.length) return null;
    var picked = variants.filter(function (v) { return v.id === giftVariant[i]; })[0] || variants[0];
    return { tier: i, product: product, variants: variants, variant: picked, selected: giftPicked[i] === true };
  }

  function addToBundle(variantId, quantity) {
    var n = Math.max(1, quantity || 1), added = false;
    for (var k = 0; k < n; k++) {
      if (!byVariant[variantId] || !canAdd(bundle.length, DATA.maxItems, 1)) break;
      bundle.push(variantId);
      added = true;
    }
    if (added) { save(); update(); }
  }

  function removeFromBundle(variantId) {
    bundle = removeLast(bundle, variantId);
    save();
    update();
  }

  function removeAt(index) {
    bundle = bundle.slice(0, index).concat(bundle.slice(index + 1));
    save();
    update();
  }

  /* ---------- markup ---------- */

  function priceHtml(shown, labels) {
    if (shown.compare === null) {
      return '<span class="wow-bundle__money' + (labels.plainSale ? ' wow-bundle__money--sale' : '') + '">' +
        '<span class="wow-bundle__sr">' + labels.plain + '</span> <span>' + money(shown.price) + '</span></span>';
    }
    return '<span class="wow-bundle__money wow-bundle__money--sale">' +
      '<span class="wow-bundle__sr">' + labels.sale + '</span> <span>' + money(shown.price) + '</span></span> ' +
      '<span class="wow-bundle__money wow-bundle__money--compare">' +
      '<span class="wow-bundle__sr">' + labels.compare + '</span> <span>' + money(shown.compare) + '</span></span>';
  }

  var PRICE_LABELS = { plain: 'Price', sale: 'Sale price', compare: 'Original price' };

  function reviewHtml(p) {
    if (!(Number(p.rating_count) > 0)) return '';
    var pct = Math.max(0, Math.min(100, (Number(p.rating) / 5) * 100));
    return '<div class="wow-bundle__review" aria-label="product star rating">' +
      '<span class="wow-bundle__stars">' +
        '<span class="wow-bundle__sr">' + escapeHtml(String(p.rating)) + ' stars out of 5 stars</span> ' +
        '<span class="wow-bundle__stars-back"></span> ' +
        '<span class="wow-bundle__stars-front" style="width: ' + pct.toFixed(4) + '%;"></span>' +
      '</span> ' +
      '<span class="wow-bundle__review-count">(' +
        ' ' + Number(p.rating_count) + ' Reviews )</span>' +
    '</div>';
  }

  function variantOf(p) {
    var id = chosen[p.handle];
    return p.variants.filter(function (v) { return v.id === id; })[0] || p.variants[0];
  }

  function cardHtml(p, position) {
    var v = variantOf(p);
    var href = escapeHtml(p.url + '?variant=' + v.id);
    var options = p.has_only_default_variant ? '' :
      '<div class="wow-bundle__options">' +
        '<select class="wow-bundle__select" data-wow-variant aria-label="variant of ' + escapeHtml(p.title) + '">' +
          p.variants.map(function (o) {
            return '<option value="' + o.id + '"' + (o.id === v.id ? ' selected' : '') + '>' + escapeHtml(o.title) + '</option>';
          }).join('') +
        '</select>' +
      '</div>';
    return '' +
      '<div class="wow-bundle__card" data-handle="' + escapeHtml(p.handle) + '" role="group" aria-roledescription="slide" aria-label="' + escapeHtml(p.title) + '">' +
        '<div class="wow-bundle__product">' +
          '<div class="wow-bundle__media">' +
            '<a class="wow-bundle__image" data-wow-link href="' + href + '" rel="nofollow">' +
              '<img data-wow-image src="' + escapeHtml(img(v.image || p.image, 400)) + '" alt="' + escapeHtml(p.title) + '" loading="' + (position < 2 ? 'eager' : 'lazy') + '">' +
            '</a>' +
          '</div>' +
          '<div class="wow-bundle__info">' +
            '<h5 class="wow-bundle__title"><a class="wow-bundle__title-link" data-wow-link href="' + href + '" rel="nofollow" aria-label="View ' + escapeHtml(p.title) + '">' + escapeHtml(p.title) + '</a></h5>' +
            reviewHtml(p) +
            '<div class="wow-bundle__price" data-wow-price></div>' +
          '</div>' +
          options +
          '<div class="wow-bundle__actions">' +
            '<button type="button" class="wow-bundle__button wow-bundle__button--secondary" data-wow-quick-view>' + escapeHtml(T.quickView) + '</button>' +
            '<div class="wow-bundle__quantity" data-wow-quantity></div>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function quantityHtml(p, v) {
    var n = countOf(bundle, v.id);
    if (!n) {
      return '<button type="button" class="wow-bundle__button" data-wow-add aria-label="Add the product, ' + escapeHtml(p.title) + ' to Cart">' +
        '<span>' + escapeHtml(T.addToBundle) + '</span></button>';
    }
    return '<div class="wow-bundle__stepper">' +
      '<button type="button" class="wow-bundle__stepper-button" data-wow-less aria-label="Decrease quantity of ' + escapeHtml(p.title) + '">' + ICON.minus + '</button>' +
      '<span class="wow-bundle__stepper-label">' + n + '</span>' +
      '<button type="button" class="wow-bundle__stepper-button" data-wow-more aria-label="Increase quantity of ' + escapeHtml(p.title) + '">' + ICON.plus + '</button>' +
    '</div>';
  }

  function stepHtml(step, index) {
    var first = index === 0;
    return '' +
      '<div class="wow-bundle__step" data-wow-step="' + index + '" aria-label="step" role="listitem">' +
        '<div class="wow-bundle__step-header">' +
          '<div class="wow-bundle__step-copy">' +
            '<h3 class="wow-bundle__step-title">' + escapeHtml(step.title) + '</h3>' +
            '<p class="wow-bundle__step-description">' + escapeHtml(step.description) + '</p>' +
          '</div>' +
          '<div class="wow-bundle__step-actions">' +
            '<button type="button" class="wow-bundle__scroll" data-wow-scroll="' + index + '" aria-label="scroll to top">' +
              ICON.up + '<span>' + escapeHtml(first ? T.top : T.prev) + '</span>' +
            '</button>' +
          '</div>' +
        '</div>' +
        '<div class="wow-bundle__grid" data-wow-carousel role="region" aria-roledescription="carousel">' +
          '<div class="wow-bundle__arrows">' +
            '<button type="button" class="wow-bundle__arrow wow-bundle__arrow--prev" data-wow-arrow="-1" aria-label="Previous slide">' + ICON.arrow + '</button>' +
            '<button type="button" class="wow-bundle__arrow wow-bundle__arrow--next" data-wow-arrow="1" aria-label="Next slide">' + ICON.arrow + '</button>' +
          '</div>' +
          '<div class="wow-bundle__track">' +
            '<div class="wow-bundle__list" data-wow-list>' +
              step.products.map(cardHtml).join('') +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function shellHtml() {
    return '' +
      '<div class="wow-bundle__main">' +
        '<div class="wow-bundle__header">' +
          '<h1 class="wow-bundle__super-title">' + escapeHtml(T.superTitle) + '</h1>' +
          '<h2 class="wow-bundle__main-title">' + escapeHtml(T.title) + '</h2>' +
          '<p class="wow-bundle__description">' + escapeHtml(T.description) + '</p>' +
        '</div>' +
        '<div class="wow-bundle__body">' +
          '<div class="wow-bundle__steps" role="list">' + steps.map(stepHtml).join('') + '</div>' +
          '<div class="wow-bundle__box" data-wow-box></div>' +
        '</div>' +
      '</div>' +
      '<div class="wow-bundle__backdrop" data-wow-collapse hidden></div>';
  }

  function progressHtml() {
    var list = tiers();
    if (!list.length) return '';
    var count = bundle.length;
    var fill = progressPercent(count, list);
    return '' +
      '<div class="wow-bundle__progress">' +
        '<div class="wow-bundle__marks">' +
          list.map(function (t, i) {
            return '<div class="wow-bundle__mark' + (count >= t.quantity ? ' is-complete' : '') + '" style="left: ' + tierPosition(t, list) + '%;">' +
              '<div class="wow-bundle__mark-icon"><span class="wow-bundle__mark-value">' + percentLabel(t.percent) + '</span></div>' +
              '<span class="wow-bundle__mark-label">' + escapeHtml(fillTemplate(T.progressLabel, t)) + '</span>' +
            '</div>';
          }).join('') +
        '</div>' +
        '<div class="wow-bundle__meter' + (fill ? '' : ' is-empty') + '">' +
          '<div class="wow-bundle__meter-fill" role="progressbar" tabindex="0" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + fill + '" aria-label="Bundle tier progress" style="width: ' + fill + '%;">' +
            '<span class="wow-bundle__sr">Bundle progress: ' + fill + '% complete</span>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function giftHtml() {
    var gift = currentGift();
    if (!gift) return '';
    var p = gift.product;
    var select = gift.variants.length > 1
      ? '<div class="wow-bundle__gift-options">' +
          '<select class="wow-bundle__select wow-bundle__select--gift" data-wow-gift-variant aria-label="variant of ' + escapeHtml(p.title) + '">' +
            gift.variants.map(function (v) {
              return '<option value="' + v.id + '"' + (v.id === gift.variant.id ? ' selected' : '') + '>' + escapeHtml(v.title) + '</option>';
            }).join('') +
          '</select>' +
        '</div>'
      : '';
    var action = gift.selected
      ? '<button type="button" class="wow-bundle__gift-button wow-bundle__gift-button--remove" data-wow-gift-remove aria-label="Remove gift: ' + escapeHtml(p.title) + '">' + ICON.times + '</button>'
      : '<button type="button" class="wow-bundle__button wow-bundle__gift-button" data-wow-gift-add><span>' + escapeHtml(T.selectGift) + '</span></button>';
    return '' +
      '<div class="wow-bundle__gifts">' +
        '<h3 class="wow-bundle__gift-header">' + escapeHtml(TIERS[gift.tier].giftHeader) + '</h3>' +
        '<div class="wow-bundle__gift">' +
          '<div class="wow-bundle__gift-image">' +
            '<img src="' + escapeHtml(img(gift.variant.image || p.image, 160)) + '" alt="View ' + escapeHtml(p.title) + '">' +
          '</div>' +
          '<div class="wow-bundle__gift-info">' +
            '<div><h3 class="wow-bundle__gift-title">' + escapeHtml(p.title) + '</h3></div>' +
            select +
            '<div class="wow-bundle__gift-actions">' + action + '</div>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function rowHtml(id, index, percent) {
    var hit = byVariant[id], p = hit.product, v = hit.variant;
    var variantLine = p.has_only_default_variant ? '' :
      '<p class="wow-bundle__row-variant">' + escapeHtml(v.title) + '</p>';
    var shown = displayPrice(v, percent);
    return '' +
      '<div class="wow-bundle__slot" aria-label="' + escapeHtml(p.title) + '" role="listitem">' +
        '<div class="wow-bundle__row">' +
          '<div class="wow-bundle__row-left">' +
            '<div class="wow-bundle__row-media"><img src="' + escapeHtml(img(v.image || p.image, 160)) + '" alt="View ' + escapeHtml(p.title) + '" width="80" height="80"></div>' +
            '<div class="wow-bundle__row-info">' +
              '<h3 class="wow-bundle__row-title">' + escapeHtml(p.title) + '</h3>' +
              variantLine +
              '<div class="wow-bundle__row-price"><div>' + priceHtml(shown, PRICE_LABELS) + '</div></div>' +
            '</div>' +
          '</div>' +
          '<div class="wow-bundle__row-right">' +
            '<button type="button" class="wow-bundle__row-remove" data-wow-remove="' + index + '" aria-label="Remove ' + escapeHtml(p.title) + ' from bundle">' + ICON.times + '</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function emptySlotHtml(position) {
    var tier = tiers().filter(function (t) { return t.quantity === position; })[0];
    return '' +
      '<div class="wow-bundle__slot" aria-label="Bundle product" role="listitem">' +
        '<div class="wow-bundle__row wow-bundle__row--empty">' +
          '<div class="wow-bundle__placeholder"><span class="wow-bundle__placeholder-icon">' + ICON.plus + '</span></div>' +
          '<div class="wow-bundle__placeholder-detail">' +
            '<h3 class="wow-bundle__placeholder-title">' + escapeHtml(T.selectProduct) + '</h3>' +
            (tier ? '<p class="wow-bundle__placeholder-description">' + escapeHtml(fillTemplate(T.holderLabel, tier)) + '</p>' : '') +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function ctaHtml(label) {
    var totals = bundleTotals(bundleVariants(), tiers());
    var price = '';
    if (bundle.length) {
      price = totals.percent > 0
        ? priceHtml({ price: totals.discounted, compare: totals.original },
          { sale: 'Sale subtotal price', compare: 'Original subtotal price' })
        : priceHtml({ price: totals.original, compare: null },
          { plain: 'Subtotal price', plainSale: true });
    }
    var off = busy || bundle.length < DATA.minItems;
    return '' +
      '<button type="button" class="wow-bundle__button wow-bundle__cta" data-wow-cart' + (off ? ' disabled="disabled"' : '') + ' aria-label="Add all selected products to cart">' +
        '<span>' + escapeHtml(label || T.addToCart) + '</span> ' +
        '<span data-wow-cta-price>' + (label ? '' : price) + '</span>' +
      '</button>';
  }

  function boxHtml() {
    var percent = percentNow();
    var totals = bundleTotals(bundleVariants(), tiers());
    var rows = bundle.map(function (id, i) { return rowHtml(id, i, percent); });
    for (var n = bundle.length + 1; n <= DATA.maxItems; n++) rows.push(emptySlotHtml(n));
    var savings = totals.savings > 0
      ? '<div class="wow-bundle__savings">' +
          '<span class="wow-bundle__savings-label">' + escapeHtml(T.totalSavings) + '</span> ' +
          '<span class="wow-bundle__savings-amount">' + money(totals.savings) + '</span>' +
        '</div>'
      : '';
    return '' +
      '<div class="wow-bundle__box-inner">' +
        '<div class="wow-bundle__box-header">' +
          '<div class="wow-bundle__box-header-left">' +
            '<h3 class="wow-bundle__box-title">' + escapeHtml(T.containerTitle) + '</h3>' +
            '<span class="wow-bundle__box-count"><span>' + bundle.length + '</span> <span>/</span> <span>' + DATA.maxItems + '</span></span>' +
          '</div>' +
          '<button type="button" class="wow-bundle__expand" data-wow-expand aria-expanded="' + (expanded ? 'true' : 'false') + '" aria-label="' + (expanded ? 'Collapse' : 'Expand') + ' bundle container">' +
            (expanded ? ICON.down : ICON.up) +
          '</button>' +
        '</div>' +
        progressHtml() +
        giftHtml() +
        '<div class="wow-bundle__box-body">' +
          '<div class="wow-bundle__rows" role="list" aria-label="Selected bundle products">' + rows.join('') + '</div>' +
          savings +
        '</div>' +
        '<div class="wow-bundle__box-action"><div class="wow-bundle__cta-wrap" data-wow-cta>' + ctaHtml() + '</div></div>' +
      '</div>';
  }

  /* ---------- painting ---------- */

  function findProduct(handle) {
    for (var s = 0; s < steps.length; s++) {
      for (var i = 0; i < steps[s].products.length; i++) {
        if (steps[s].products[i].handle === handle) return steps[s].products[i];
      }
    }
    return null;
  }

  function paintCard(card) {
    var p = findProduct(card.getAttribute('data-handle'));
    if (!p) return;
    var v = variantOf(p);
    card.querySelector('[data-wow-price]').innerHTML =
      '<div>' + priceHtml(displayPrice(v, percentNow()), PRICE_LABELS) + '</div>';
    card.querySelector('[data-wow-quantity]').innerHTML = quantityHtml(p, v);
    var picture = card.querySelector('[data-wow-image]');
    var src = img(v.image || p.image, 400);
    if (picture && picture.getAttribute('src') !== src) picture.setAttribute('src', src);
    var links = card.querySelectorAll('[data-wow-link]');
    for (var i = 0; i < links.length; i++) links[i].setAttribute('href', p.url + '?variant=' + v.id);
    var select = card.querySelector('[data-wow-variant]');
    if (select && Number(select.value) !== v.id) select.value = String(v.id);
  }

  function paintCards() {
    var cards = el.querySelectorAll('.wow-bundle__card');
    for (var i = 0; i < cards.length; i++) paintCard(cards[i]);
  }

  function paintBox() {
    var box = el.querySelector('[data-wow-box]');
    var list = box.querySelector('.wow-bundle__rows');
    var scroll = list ? list.scrollTop : 0;
    box.innerHTML = boxHtml();
    box.classList.toggle('is-expanded', expanded);
    list = box.querySelector('.wow-bundle__rows');
    if (list) list.scrollTop = scroll;
  }

  function update() {
    paintCards();
    paintBox();
  }

  function setExpanded(on) {
    expanded = on;
    doc.documentElement.classList.toggle('wow-bundle-open', on);
    el.querySelector('.wow-bundle__backdrop').hidden = !on;
    paintBox();
  }

  /* ---------- carousel ---------- */

  function makeCarousel(node) {
    var list = node.querySelector('[data-wow-list]');
    var total = list.children.length;
    var index = 0, startX = null, moved = false;
    function per() { return perPage(root.innerWidth); }
    function paint() {
      var n = per();
      var max = Math.max(0, total - n);
      if (index > max) index = max;
      for (var i = 0; i < list.children.length; i++) list.children[i].style.width = 'calc(' + (100 / n) + '%)';
      list.style.transform = 'translateX(' + (-(index * 100) / n) + '%)';
      node.classList.toggle('is-overflow', total > n);
      var prev = node.querySelector('[data-wow-arrow="-1"]');
      var next = node.querySelector('[data-wow-arrow="1"]');
      prev.setAttribute('aria-label', index <= 0 ? 'Go to last slide' : 'Previous slide');
      next.setAttribute('aria-label', index >= max ? 'Go to first slide' : 'Next slide');
      for (var k = 0; k < list.children.length; k++) {
        var hidden = k < index || k >= index + n;
        if (hidden) list.children[k].setAttribute('aria-hidden', 'true');
        else list.children[k].removeAttribute('aria-hidden');
      }
    }
    function go(direction) { index = carouselStep(index, direction, total, per()); paint(); }
    node.addEventListener('click', function (ev) {
      var arrow = ev.target.closest('[data-wow-arrow]');
      if (arrow) go(Number(arrow.getAttribute('data-wow-arrow')));
    });
    // a sideways swipe turns the page; taps and up-and-down scrolling pass through
    list.addEventListener('touchstart', function (ev) {
      startX = ev.touches[0].clientX; moved = false;
    }, { passive: true });
    list.addEventListener('touchmove', function (ev) {
      if (startX === null || moved) return;
      var dx = ev.touches[0].clientX - startX;
      if (Math.abs(dx) > 50) { moved = true; go(dx < 0 ? 1 : -1); }
    }, { passive: true });
    list.addEventListener('touchend', function () { startX = null; });
    paint();
    return { paint: paint };
  }

  /* ---------- quick view ---------- */

  var modalState = null, modalFocus = null;

  function modalHtml() {
    var p = modalState.product, v = modalState.variant;
    var main = modalState.image || v.image || p.image;
    var thumbs = (p.images || []).length > 1
      ? '<div class="wow-bundle__modal-thumbs">' +
          p.images.map(function (src) {
            return '<div class="wow-bundle__modal-thumb' + (img(src, 400) === img(main, 400) ? ' is-active' : '') + '" role="button" tabindex="0" data-wow-thumb="' + escapeHtml(src) + '">' +
              '<img src="' + escapeHtml(img(src, 200)) + '" alt="' + escapeHtml(p.title) + '"></div>';
          }).join('') +
        '</div>'
      : '';
    // a product with one variant still gets the (empty) options row: it is
    // one more gap in the column, as in Rebuy's
    var options = p.has_only_default_variant ? '<div class="wow-bundle__modal-options"><span></span></div>' :
      '<div class="wow-bundle__modal-options"><div>' +
        '<select class="wow-bundle__select" data-wow-modal-variant name="variant-selector" aria-label="variant of ' + escapeHtml(p.title) + '">' +
          p.variants.map(function (o) {
            return '<option value="' + o.id + '"' + (o.id === v.id ? ' selected' : '') + '>' + escapeHtml(o.title) + '</option>';
          }).join('') +
        '</select>' +
      '</div></div>';
    return '' +
      '<div class="wow-bundle__modal-dialog" role="dialog" aria-modal="true" aria-label="' + escapeHtml(T.modalTitle) + '" tabindex="-1">' +
        '<button type="button" class="wow-bundle__modal-close" data-wow-modal-close aria-label="Close modal">' + ICON.times + '</button>' +
        '<div class="wow-bundle__modal-header">' + escapeHtml(T.modalTitle) + '</div>' +
        '<div class="wow-bundle__modal-body">' +
          '<div class="wow-bundle__modal-product">' +
            '<div class="wow-bundle__modal-media">' +
              '<img class="wow-bundle__modal-image" src="' + escapeHtml(img(main, 400)) + '" alt="' + escapeHtml(p.title) + '">' +
              thumbs +
            '</div>' +
            '<div class="wow-bundle__modal-info">' +
              '<h4 class="wow-bundle__modal-title">' + escapeHtml(p.title) + '</h4>' +
              '<div class="wow-bundle__modal-price"><div><span class="wow-bundle__money wow-bundle__money--modal">' + money(v.price) + '</span></div></div>' +
              '<div class="wow-bundle__modal-stock"><span class="wow-bundle__modal-stock-dot"></span><span class="wow-bundle__modal-stock-label">' + escapeHtml(T.inStock) + '</span></div>' +
              '<div><div class="wow-bundle__modal-stepper">' +
                '<button type="button" class="wow-bundle__modal-stepper-button" data-wow-modal-qty="-1" aria-label="Decrease quantity of ' + escapeHtml(p.title) + '">' + ICON.minus + '</button>' +
                '<span class="wow-bundle__modal-stepper-label">' + modalState.quantity + '</span>' +
                '<button type="button" class="wow-bundle__modal-stepper-button" data-wow-modal-qty="1" aria-label="Increase quantity of ' + escapeHtml(p.title) + '">' + ICON.plus + '</button>' +
              '</div></div>' +
              options +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div class="wow-bundle__modal-actions">' +
          '<button type="button" class="wow-bundle__button" data-wow-modal-add>' + escapeHtml(T.addToBundle) + '</button>' +
          '<button type="button" class="wow-bundle__button wow-bundle__button--secondary" data-wow-modal-close>' + escapeHtml(T.dismiss) + '</button>' +
        '</div>' +
      '</div>';
  }

  function paintModal(keepFocus) {
    modal.innerHTML = modalHtml();
    if (!keepFocus) modal.querySelector('.wow-bundle__modal-dialog').focus();
  }

  function openModal(p) {
    modalFocus = doc.activeElement;
    modalState = { product: p, variant: variantOf(p), quantity: 1, image: null };
    modal.hidden = false;
    doc.documentElement.classList.add('wow-bundle-open');
    paintModal();
  }

  function closeModal() {
    modal.hidden = true;
    modal.innerHTML = '';
    modalState = null;
    if (!expanded) doc.documentElement.classList.remove('wow-bundle-open');
    if (modalFocus && modalFocus.focus) modalFocus.focus();
  }

  function bindModal() {
    modal.addEventListener('click', function (ev) {
      var t = ev.target;
      if (t === modal || t.closest('[data-wow-modal-close]')) { closeModal(); return; }
      var qty = t.closest('[data-wow-modal-qty]');
      if (qty) {
        modalState.quantity = Math.max(1, modalState.quantity + Number(qty.getAttribute('data-wow-modal-qty')));
        paintModal(true);
        return;
      }
      var thumb = t.closest('[data-wow-thumb]');
      if (thumb) { modalState.image = thumb.getAttribute('data-wow-thumb'); paintModal(true); return; }
      if (t.closest('[data-wow-modal-add]')) {
        var p = modalState.product, v = modalState.variant, n = modalState.quantity;
        chosen[p.handle] = v.id;
        closeModal();
        addToBundle(v.id, n);
        update();
      }
    });
    modal.addEventListener('change', function (ev) {
      var select = ev.target.closest('[data-wow-modal-variant]');
      if (!select) return;
      var id = Number(select.value);
      modalState.variant = modalState.product.variants.filter(function (v) { return v.id === id; })[0] || modalState.variant;
      modalState.image = null;
      paintModal(true);
    });
    modal.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') { closeModal(); return; }
      if ((ev.key === 'Enter' || ev.key === ' ') && ev.target.closest('[data-wow-thumb]')) {
        ev.preventDefault();
        modalState.image = ev.target.closest('[data-wow-thumb]').getAttribute('data-wow-thumb');
        paintModal(true);
        return;
      }
      if (ev.key !== 'Tab') return;
      var f = modal.querySelectorAll('button:not([disabled]), select, [tabindex="0"]');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (ev.shiftKey && (doc.activeElement === first || !modal.contains(doc.activeElement))) { ev.preventDefault(); last.focus(); }
      else if (!ev.shiftKey && doc.activeElement === last) { ev.preventDefault(); first.focus(); }
    });
  }

  /* ---------- add to cart ---------- */

  function rebuyBuilder() {
    var node = doc.getElementById('rebuy-widget-' + DATA.rebuyWidgetId);
    return node && node.children.length ? node : null;
  }

  // The cart did not honour what the builder showed. Never show those prices
  // again this visit: hand the page back to Rebuy's builder, or, once Rebuy is
  // gone, carry on at plain prices with no tiers.
  function standDown(check) {
    try { root.sessionStorage.setItem(OFF_KEY, '1'); } catch (e) { /* ignore */ }
    if (root.console) root.console.error('[wow-bundle] cart did not match the builder', check);
    if (rebuyBuilder()) {
      if (expanded) setExpanded(false);
      doc.documentElement.classList.remove('wow-bundle-ready', 'wow-bundle-open');
      el.hidden = true;
      return;
    }
    noDiscount = true;
    update();
  }

  function setCta(label) {
    var wrap = el.querySelector('[data-wow-cta]');
    if (wrap) wrap.innerHTML = ctaHtml(label);
  }

  function addBundleToCart() {
    if (busy || bundle.length < DATA.minItems) return;
    var gift = currentGift();
    var id = newBundleId();
    var expected = bundleTotals(bundleVariants(), tiers()).discounted;
    var items = cartItems(bundle, gift && gift.selected ? gift.variant.id : null, id,
      DATA.borrowRebuyDiscount === true ? DATA.rebuyWidgetId : null);
    busy = true;
    setCta(T.addingToCart);
    api('/cart/add.js', { items: items })
      .then(function () { return api('/cart.js'); })
      .then(function (cart) {
        var check = verifyCart(cart, id, expected);
        // a "free" gift that came back with a price must not stay in the cart
        var fix = Promise.resolve();
        if (check.chargedGiftKeys.length) {
          var updates = {};
          check.chargedGiftKeys.forEach(function (key) { updates[key] = 0; });
          fix = api('/cart/update.js', { updates: updates }).catch(function () {});
        }
        return fix.then(function () { return check; });
      })
      .then(function (check) {
        bundle = [];
        giftPicked = {};
        save();
        busy = false;
        if (expanded) setExpanded(false);
        update();
        // The WOW cart opens by itself on a cart add. Asked for out loud as
        // well, so taking a gift back out cannot leave it shut.
        if (root.WowCart && root.WowCart.ready && root.WowCart.open) root.WowCart.open(true);
        if (!check.ok && !noDiscount) { standDown(check); return; }
        setCta(T.addedToCart);
        root.setTimeout(function () { if (!busy) setCta(); }, 2000);
      })
      .catch(function (e) {
        busy = false;
        setCta();
        if (root.console) root.console.error('[wow-bundle] add to cart failed', e);
      });
  }

  /* ---------- events ---------- */

  function cardOf(node) {
    var card = node.closest('.wow-bundle__card');
    return card ? findProduct(card.getAttribute('data-handle')) : null;
  }

  function scrollToStep(index) {
    var target = index <= 0 ? el : el.querySelector('[data-wow-step="' + (index - 1) + '"]');
    if (target && target.scrollIntoView) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function bind() {
    el.addEventListener('click', function (ev) {
      var t = ev.target, p;
      if (t.closest('[data-wow-add]') || t.closest('[data-wow-more]')) {
        p = cardOf(t);
        if (p) addToBundle(variantOf(p).id, 1);
        return;
      }
      if (t.closest('[data-wow-less]')) {
        p = cardOf(t);
        if (p) removeFromBundle(variantOf(p).id);
        return;
      }
      if (t.closest('[data-wow-quick-view]')) {
        p = cardOf(t);
        if (p) openModal(p);
        return;
      }
      var scroll = t.closest('[data-wow-scroll]');
      if (scroll) { scrollToStep(Number(scroll.getAttribute('data-wow-scroll'))); return; }
      var remove = t.closest('[data-wow-remove]');
      if (remove) { removeAt(Number(remove.getAttribute('data-wow-remove'))); return; }
      if (t.closest('[data-wow-expand]')) { setExpanded(!expanded); return; }
      if (t.closest('[data-wow-collapse]')) { setExpanded(false); return; }
      var gift = currentGift();
      if (t.closest('[data-wow-gift-add]') && gift) { giftPicked[gift.tier] = true; paintBox(); return; }
      if (t.closest('[data-wow-gift-remove]') && gift) { giftPicked[gift.tier] = false; paintBox(); return; }
      if (t.closest('[data-wow-cart]')) addBundleToCart();
    });
    el.addEventListener('change', function (ev) {
      var select = ev.target.closest('[data-wow-variant]');
      if (select) {
        var p = cardOf(select);
        if (p) { chosen[p.handle] = Number(select.value); paintCard(select.closest('.wow-bundle__card')); }
        return;
      }
      var giftSelect = ev.target.closest('[data-wow-gift-variant]');
      var gift = currentGift();
      if (giftSelect && gift) { giftVariant[gift.tier] = Number(giftSelect.value); paintBox(); }
    });
    var timer = null;
    root.addEventListener('resize', function () {
      root.clearTimeout(timer);
      timer = root.setTimeout(function () {
        carousels.forEach(function (c) { c.paint(); });
        // the pulled-up panel only exists on small screens
        if (expanded && root.innerWidth >= 1180) setExpanded(false);
      }, 100);
    });
    doc.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && expanded && !modalState) setExpanded(false);
    });
  }

  /* ---------- start ---------- */

  function previewAsked() {
    try {
      var m = root.location.search.match(/[?&]wow_bundle=(preview|off)\b/);
      if (m) root.sessionStorage.setItem(PREVIEW_KEY, m[1]);
      return root.sessionStorage.getItem(PREVIEW_KEY) === 'preview';
    } catch (e) { return false; }
  }

  function stoodDown() {
    try { return root.sessionStorage.getItem(OFF_KEY) === '1'; } catch (e) { return false; }
  }

  function mount() {
    var rebuy = doc.querySelector('[data-rebuy-id="' + DATA.rebuyWidgetId + '"]') ||
      doc.getElementById('rebuy-widget-' + DATA.rebuyWidgetId);
    el = doc.createElement('div');
    el.id = 'wow-bundle';
    el.className = 'wow-bundle';
    el.innerHTML = shellHtml();
    if (rebuy && rebuy.parentNode) {
      rebuy.parentNode.insertBefore(el, rebuy);
    } else {
      var main = doc.getElementById('MainContent') || doc.body;
      var holder = doc.createElement('div');
      holder.className = 'page-width';
      holder.appendChild(el);
      main.appendChild(holder);
    }
    modal = doc.createElement('div');
    modal.className = 'wow-bundle__modal';
    modal.hidden = true;
    doc.body.appendChild(modal);
  }

  function start() {
    var node = doc.getElementById('wow-bundle-data');
    if (!node) return;                       // not the bundle builder page
    DATA = JSON.parse(node.textContent);
    if (!isLive(DATA, previewAsked())) return;   // discount not live: Rebuy keeps the page
    T = DATA.text || {};
    TIERS = DATA.tiers || [];
    if (stoodDown()) {
      if (rebuyBuilder() || doc.querySelector('[data-rebuy-id="' + DATA.rebuyWidgetId + '"]')) return;
      noDiscount = true;
    }
    steps = (DATA.steps || []).map(function (s) {
      return { title: s.title, description: s.description, products: prepareProducts(s.products, s.handles) };
    }).filter(function (s) { return s.products.length > 0; });
    if (!steps.length) return;               // nothing to show: Rebuy keeps the page
    steps.forEach(function (s) {
      s.products.forEach(function (p) {
        p.variants.forEach(function (v) { byVariant[v.id] = { product: p, variant: v }; });
      });
    });
    load();
    mount();
    var nodes = el.querySelectorAll('[data-wow-carousel]');
    for (var i = 0; i < nodes.length; i++) carousels.push(makeCarousel(nodes[i]));
    bind();
    bindModal();
    update();
    root.WowBundle = { ready: true };
    doc.documentElement.classList.add('wow-bundle-ready');
  }

  function safeStart() {
    try {
      start();
    } catch (e) {
      // Anything going wrong here must leave the shopper with Rebuy's builder.
      doc.documentElement.classList.remove('wow-bundle-ready', 'wow-bundle-open');
      if (el && el.parentNode) el.parentNode.removeChild(el);
      if (modal && modal.parentNode) modal.parentNode.removeChild(modal);
      if (root.WowBundle) root.WowBundle.ready = false;
      if (root.console) root.console.error('[wow-bundle] start failed', e);
    }
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', safeStart);
  else safeStart();
})(typeof window !== 'undefined' ? window : this);
